/**
 * Date range filters for the admin lists. Values are what native inputs hold, in local time:
 * `YYYY-MM-DD` (date mode) or `YYYY-MM-DDTHH:mm` (datetime mode); empty means open-ended.
 */
export type DateRangeMode = 'date' | 'datetime';

type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'last7Days'
  | 'last30Days'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisYear'
  | 'lastHour'
  | 'last24Hours';

export interface DateRangeValue {
  from: string;
  to: string;
  /** Set when the range came from a preset, so it is labelled by name (relative presets drift with "now"). */
  preset?: DateRangePreset;
}

export const EMPTY_DATE_RANGE: DateRangeValue = { from: '', to: '' };

export const DATE_PRESETS: Record<DateRangeMode, DateRangePreset[]> = {
  date: ['today', 'yesterday', 'last7Days', 'last30Days', 'thisMonth', 'lastMonth', 'thisYear'],
  datetime: ['lastHour', 'last24Hours', 'last7Days', 'last30Days'],
};

const pad = (n: number) => String(n).padStart(2, '0');
const toDateInput = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toDateTimeInput = (d: Date) => `${toDateInput(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

const addDays = (d: Date, days: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);

/** The range a preset stands for at `now`. Datetime presets are open-ended ("until now"). */
export const presetRange = (preset: DateRangePreset, mode: DateRangeMode, now = new Date()): DateRangeValue => {
  if (mode === 'datetime') {
    const hours = { lastHour: 1, last24Hours: 24, last7Days: 7 * 24, last30Days: 30 * 24 }[preset as string] ?? 24;
    return { from: toDateTimeInput(new Date(now.getTime() - hours * 3_600_000)), to: '', preset };
  }
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const range = (from: Date, to: Date): DateRangeValue => ({ from: toDateInput(from), to: toDateInput(to), preset });
  switch (preset) {
    case 'yesterday':
      return range(addDays(today, -1), addDays(today, -1));
    case 'last7Days':
      return range(addDays(today, -6), today);
    case 'last30Days':
      return range(addDays(today, -29), today);
    case 'thisMonth':
      return range(new Date(today.getFullYear(), today.getMonth(), 1), today);
    case 'lastMonth':
      return range(
        new Date(today.getFullYear(), today.getMonth() - 1, 1),
        new Date(today.getFullYear(), today.getMonth(), 0),
      );
    case 'thisYear':
      return range(new Date(today.getFullYear(), 0, 1), today);
    default:
      return range(today, today);
  }
};

const parseLocal = (value: string): Date | undefined => {
  if (!value) return undefined;
  // `new Date('YYYY-MM-DD')` would be UTC midnight; append a time so both forms parse as local time.
  const d = new Date(value.length === 10 ? `${value}T00:00` : value);
  return isNaN(d.getTime()) ? undefined : d;
};

/**
 * ISO instants for the API, both bounds inclusive: a date covers the whole local day, a datetime the
 * whole minute.
 */
export const dateRangeToIso = (value: DateRangeValue): { from?: string; to?: string } => {
  const from = parseLocal(value.from);
  const to = parseLocal(value.to);
  const toEnd = to && (value.to.length === 10 ? addDays(to, 1).getTime() - 1 : to.getTime() + 59_999);
  return { from: from?.toISOString(), to: toEnd !== undefined ? new Date(toEnd).toISOString() : undefined };
};

const formatBound = (value: string) => {
  const d = parseLocal(value);
  if (!d) return value;
  return value.length === 10
    ? d.toLocaleDateString()
    : d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
};

/** "01/09/2026 – 15/09/2026", "from 01/09/2026", "until 15/09/2026", or undefined when empty. */
export const formatDateRange = (value: DateRangeValue, words: { from: string; until: string }): string | undefined => {
  if (value.from && value.to) {
    return value.from === value.to ? formatBound(value.from) : `${formatBound(value.from)} – ${formatBound(value.to)}`;
  }
  if (value.from) return `${words.from} ${formatBound(value.from)}`;
  if (value.to) return `${words.until} ${formatBound(value.to)}`;
  return undefined;
};
